import _ from 'lodash';
import { StoryHistory } from 'client/utils';
import { CardData, StoryModel, StoryStep, CardDataWithStepInfo, StoryVersionType, StorySymbols } from 'types/story';
import { CountType } from 'utils/facades/navigation-card-facade';

const VERSIONS = {
	latest: 'latest',
	published: 'published',
	archived: 'archived',
	unpublished: 'unpublished',
} as const;

export type RequestMainRowData = {
	storyId: StoryModel['id'];
	name: StoryModel['name'];
	organizationId: StoryModel['organizationId'];
	type: StoryModel['type'];
	language: StoryModel['language'];
	tags: StoryModel['tags'];
	teamId?: StoryModel['teamId'];
};

/**
 * Pick data from story object that needed to update story
 * @param data
 */
export function requestMainRowData(
	data: Pick<StoryModel, 'id' | 'name' | 'organizationId' | 'type' | 'language' | 'tags' | 'teamId'>
): RequestMainRowData {
	return {
		storyId: data.id,
		name: data.name,
		organizationId: data.organizationId,
		type: data.type,
		language: data.language,
		tags: data.tags,
		teamId: data.teamId || undefined,
	};
}

/**
 * @param name
 * @param storyId
 * @param teamId
 * @param organizationId
 * @param storyData
 * @constructor
 */
export class StoryFacade {
	static VERSIONS = VERSIONS;

	base: StoryModel;

	// todo: story can be undefined. need change a type and do a big refactor
	story: StoryVersionType;

	constructor(story: StoryModel, defaultVersion: string = StoryFacade.VERSIONS.latest) {
		this.base = story;
		this.story = this.getVersion(defaultVersion);
	}

	getVersion(version: string): StoryVersionType {
		return _.get(this.base, ['storyVersions', version]);
	}

	setVersion(version: string) {
		this.story = this.getVersion(version);
		return this;
	}

	/**
	 * Provide data in the format required for an update request
	 * @return {{name: *, storyData: *, storyId: *}}
	 */
	get requestData() {
		return {
			storyId: this.storyId,
			storyData: this.data,
			settings: this.settings,
		};
	}

	get requestMainRowData() {
		return requestMainRowData(this.base);
	}

	get language() {
		return this.base.language;
	}

	get settings() {
		return this.story.settings || {};
	}

	get name() {
		return this.base.name;
	}

	get isPublished() {
		return this.story.version === VERSIONS.published;
	}

	get data() {
		return this.story.data || {};
	}

	get steps(): StoryVersionType['data']['steps'] {
		return this.data.steps || [];
	}

	get elements(): StoryVersionType['data']['elements'] {
		return this.data.elements || [];
	}

	get mediaQuery(): StoryVersionType['data']['mediaQuery'] {
		return this.data.mediaQuery;
	}

	get storyId() {
		return this.base.id;
	}

	get symbols(): StorySymbols {
		return this.data.symbols || {};
	}

	get storyScore() {
		const sHistory = new StoryHistory({ storyId: this.storyId });
		return {
			[CountType.score]: () => sHistory.totalScore(this.settings, this.cards),
			[CountType.character]: () => sHistory.totalCharacterPoints(this.settings),
		};
	}

	/**
	 * Get cards array
	 * @return {Array}
	 */
	get cards(): CardData[] {
		const { steps } = this;
		const cards = _.reduce<StoryStep, CardData[]>(
			steps,
			(result, step) => {
				return result.concat(step.cards);
			},
			[]
		);

		return cards;
	}

	/**
	 * Get cards array
	 * @return {Array}
	 */
	get cardsWithStepInfo(): CardDataWithStepInfo[] {
		const { steps } = this;
		const cards = _.reduce<StoryStep, (CardData & { step: { _id: StoryStep['_id'] } })[]>(
			steps,
			(result, step) => {
				const cardsWithStep = _.map(step.cards, card => ({
					...card,
					step: { ..._.pick(step, ['_id']) },
				}));

				return result.concat(cardsWithStep);
			},
			[]
		);
		return cards;
	}

	get dataByFirstCard() {
		const result: { card: CardData | null; step: StoryStep | null } = { card: null, step: null };
		const { steps } = this;

		steps.some(step => {
			return (step.cards || []).some(card => {
				const isMatch = _.has(card, '_id');
				if (isMatch) {
					result.card = card;
					result.step = step;
				}
				return isMatch;
			});
		});
		return result;
	}

	canUseThirdPartyCookies() {
		const isSubdomainConnected = this.base.domainId !== null;
		const isEmbed = this.base.type === 'embed';
		return (isEmbed && isSubdomainConnected) || !isEmbed;
	}

	getDataByCardId(cardId): { card?: CardData; step?: StoryStep } {
		const { steps } = this;
		const result: { card?: CardData; step?: StoryStep } = {};

		steps.some(step => {
			return _.get(step, 'cards', []).some(card => {
				const isMatch = card._id === cardId;
				if (isMatch) {
					result.card = card;
					result.step = step;
				}
				return isMatch;
			});
		});

		return result;
	}

	getNextDefaultCard(step: StoryStep): CardData | undefined {
		const stepIndex: number = _.findIndex(this.steps, ['_id', step._id]);

		if (stepIndex < 0) {
			return undefined;
		}

		// get next step with cards
		const nextStep = _.find(_.slice(this.steps, stepIndex + 1), s => _.size(_.get(s, 'cards')));
		const nextStepCards = _.get(nextStep, 'cards');
		const nextCard = _.find(nextStepCards, card => card._id);

		return nextCard;
	}

	// get text with a replaced supported dynamic tet variables
	getTransformedDynamicText(text: string = '') {
		const scoreResults = this.storyScore[CountType.score]();
		/*
		const characterResults = this.storyScore[CountType.character]();

		Think what I can do with a character results.

		1. When NavigationCard last time ran I can store how much points and which character won
		and display there points as $score or $points, or I can just remember eventId and cardId which led user to
		current page
		2. It is possible by demand somehow show how much points each character got.
		 */

		return text
			.replace('$score', `${Math.ceil(scoreResults.scoreRelative) ?? '0'}/100`)
			.replace('$answers', `${scoreResults.answersTotal ?? '0'}`)
			.replace('$correct', `${scoreResults.answersCorrect ?? '0'}`);
	}
}
